"use client";

import { apiGetEventChartData, apiGetGuessChartData } from "@/api/api";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import { convertCentPriceToChancePercent } from "@/utils/price";
import { calc, get } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

// ============ Backend Data Structure ============
interface DataPoint {
    timestamp: number;
    timeStr: string;
    chance: number;
    yesPrice: string;
    noPrice: string;
}

interface ChartDataItem {
    guessNo: string;
    guessTitle?: string;
    totalTradeAmount: number;
    dataPoints: DataPoint[];
}

interface ChartDataResponse {
    eventNo: string;
    chartDataList: ChartDataItem[];
}

// ============ Chart Data Structure ============
interface ChartData {
    time: number;
    value: number;
}

interface SeriesData {
    name: string;
    title: string;
    color: string;
    data: ChartData[];
}

type TimeRangeKey = "1H" | "6H" | "1D" | "1W" | "1M" | "ALL";

interface EventChartProps {
    eventNo: string;
    guessNo?: string | null;
    volume: string;
    onTimeRangeChange?: ( range: string ) => void;
    isPreview?: boolean;
    onColumnSelect?: ( row: any ) => void;
    selectedGuessNos?: string[];
    onFilterClick?: () => void;
    sortedGuessList?: Array<{ guessNo: string; title?: string }>;
}

// Multi-series palette：色值在 globals.css `--chart-series-*`；Tailwind：`bg-chart-n`（见 tailwind.config）
// Figma: Yes/No 主推板球 图例 https://www.figma.com/design/9myWPk1URzbEOu1mNtWEPZ/Yes_No-%E4%B8%BB%E6%8E%A8%E6%9D%BF%E7%90%83?node-id=818-31246
// Figma: 轴线 / 横网格 / 刻度字 https://www.figma.com/design/9myWPk1URzbEOu1mNtWEPZ/Yes_No-%E4%B8%BB%E6%8E%A8%E6%9D%BF%E7%90%83?node-id=792-31572
// Figma: 图表区 / 时间范围切换 https://www.figma.com/design/9myWPk1URzbEOu1mNtWEPZ/Yes_No-%E4%B8%BB%E6%8E%A8%E6%9D%BF%E7%90%83?node-id=818-32211
/** Recharts stroke / SVG fill / 图例色块 backgroundColor（与 globals 中 16 条序列一致） */
export const CHART_COLORS: readonly string[] = Array.from( { length: 16 }, ( _, i ) => `var(--chart-series-${i + 1})` );

// Map time range to timeType
const timeRangeToType: Record<TimeRangeKey, number> = { "1H": 1, "6H": 2, "1D": 3, "1W": 4, "1M": 5, "ALL": 6 };

// ============ Data Transform Function ============
const transformChartData = (
    chartDataList: ChartDataItem[],
    selectedGuessNos?: string[],
    sortedGuessList?: Array<{ guessNo: string; title?: string }>,
): SeriesData[] => {
    let filteredList = chartDataList;

    // 如果传入了 selectedGuessNos，只处理选中的 guess
    if ( selectedGuessNos && selectedGuessNos.length > 0 ) {
        filteredList = chartDataList.filter( ( item ) => selectedGuessNos.includes( item.guessNo ) );
    }

    // 如果有 sortedGuessList，按照 sortedGuessList 的顺序排序 filteredList
    if ( sortedGuessList && sortedGuessList.length > 0 ) {
        filteredList = [ ...filteredList ].sort( ( a, b ) => {
            const aIndex = sortedGuessList.findIndex( ( g ) => g.guessNo === a.guessNo );
            const bIndex = sortedGuessList.findIndex( ( g ) => g.guessNo === b.guessNo );
            // 如果找不到，放在最后
            if ( aIndex === -1 && bIndex === -1 ) return 0;
            if ( aIndex === -1 ) return 1;
            if ( bIndex === -1 ) return -1;
            return aIndex - bIndex;
        } );
    }

    // 当只有一项时，使用蓝色（第二个颜色）
    const isSingleSeries = filteredList.length === 1;

    return filteredList.map( ( item, index ) => {
        // 根据排序后的 guessList 中的索引确定颜色（如果提供了 sortedGuessList）
        // 否则回退到使用 chartDataList 的原始索引
        let colorIndex: number;
        if ( sortedGuessList && sortedGuessList.length > 0 ) {
            // 使用排序后的 guessList 中的索引
            const sortedIndex = sortedGuessList.findIndex( ( g ) => g.guessNo === item.guessNo );
            colorIndex = sortedIndex >= 0 ? sortedIndex : index;
        } else {
            // 回退到原有逻辑：使用 chartDataList 的原始索引
            const originalIndex = chartDataList.findIndex( ( i ) => i.guessNo === item.guessNo );
            colorIndex = originalIndex >= 0 ? originalIndex : index;
        }

        // 当只有一项时，使用蓝色（CHART_COLORS[1] = "#086de0"）
        const color = isSingleSeries ? CHART_COLORS[1] : CHART_COLORS[colorIndex % CHART_COLORS.length];

        return {
            name: item.guessNo,
            title: get( item, "title", "" ) || get( item, "guessTitle", "" ) || item.guessNo,
            color,
            data: item.dataPoints.map( ( point ) => ( {
                time: point.timestamp,
                value: Number( convertCentPriceToChancePercent( point.yesPrice ) ),
            } ) ),
        };
    } );
};

function EventChart(
    {
        eventNo,
        guessNo,
        volume,
        onTimeRangeChange,
        isPreview = false,
        onColumnSelect,
        selectedGuessNos,
        onFilterClick,
        sortedGuessList,
    }: EventChartProps,
) {
    const [ selectedTimeRange, setSelectedTimeRange ] = useState<TimeRangeKey>( "ALL" );
    const timeRanges: TimeRangeKey[] = [ "1H", "6H", "1D", "1W", "1M", "ALL" ];
    const chartContainerRef = useRef<HTMLDivElement>( null );
    const tooltipDataRef = useRef<any>( null );
    const firstDataPointRef = useRef<any>( null );
    const onColumnSelectRef = useRef( onColumnSelect );

    // 更新回调引用
    useEffect( () => {
        onColumnSelectRef.current = onColumnSelect;
    }, [ onColumnSelect ] );

    // ============ Use Query to fetch chart data (预览模式禁用) ============
    const { data: series = [], isLoading } = useQuery( {
        queryKey: guessNo
            ? [ "eventChartData", "guess", guessNo, selectedTimeRange ]
            : [ "eventChartData", "event", eventNo, selectedTimeRange, selectedGuessNos ],
        queryFn: async () => {
            if ( guessNo ) {
                const [ isOk, res ] = await apiGetGuessChartData( {
                    guessNo,
                    timeType: timeRangeToType[selectedTimeRange],
                } );

                const dataPoints = get( res, "data.yesPriceDataPoints", [] );
                if ( isOk && dataPoints.length > 0 ) {
                    const guessTitle = sortedGuessList?.find( ( guess ) => guess.guessNo === guessNo )?.title
                        || guessNo;
                    return [ {
                        name: guessNo,
                        title: guessTitle,
                        color: CHART_COLORS[1],
                        data: dataPoints.map( ( point: any ) => ( {
                            time: get( point, "timestamp", 0 ),
                            value: Number( convertCentPriceToChancePercent( get( point, "price", "0" ) ) ),
                        } ) ),
                    } ];
                }
                return [];
            }

            const [ isOk, res ] = await apiGetEventChartData( {
                eventNo,
                timeType: timeRangeToType[selectedTimeRange],
            } );

            if ( isOk && get( res, "data.chartDataList", [] ).length > 0 ) {
                return transformChartData( get( res, "data.chartDataList", [] ), selectedGuessNos, sortedGuessList );
            }
            return [];
        },
        enabled: ( !!eventNo || !!guessNo ) && !isPreview, // 预览模式禁用请求
        // staleTime: 10 * 1000, // 10 秒内不重新请求
        refetchInterval: 5 * 1000, // 10秒轮询
    } );

    // ============ Time Range Switch ============
    const handleTimeRangeClick = ( range: TimeRangeKey ) => {
        setSelectedTimeRange( range );
        onTimeRangeChange?.( range );
    };

    // ============ Chart Data Transform ============
    const chartData = useMemo( () => {
        if ( !series || series.length === 0 ) return [];

        const allTimes = new Set<number>();
        series.forEach( ( s ) => {
            s.data.forEach( ( d ) => allTimes.add( d.time ) );
        } );

        const sortedTimes = Array.from( allTimes ).sort( ( a, b ) => a - b );

        return sortedTimes.map( ( time ) => {
            const dataPoint: any = { time };
            series.forEach( ( s ) => {
                const point = s.data.find( ( d ) => d.time === time );
                dataPoint[s.name] = point ? point.value : null;
            } );
            return dataPoint;
        } );
    }, [ series ] );

    // ============ Calculate Y Axis Domain and Ticks ============
    const { yAxisDomain, yAxisTicks } = useMemo( () => {
        if ( !chartData || chartData.length === 0 ) {
            return {
                yAxisDomain: [ 0, 100 ] as [ number, number ],
                yAxisTicks: [ 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100 ],
            };
        }

        // 1. 计算数据范围：找出所有非null值的最小值和最大值
        let min = Infinity;
        let max = -Infinity;

        chartData.forEach( ( point ) => {
            series.forEach( ( s ) => {
                const value = point[s.name];
                if ( value !== null && value !== undefined && !isNaN( value ) ) {
                    min = Math.min( min, value );
                    max = Math.max( max, value );
                }
            } );
        } );

        // 如果没有有效数据，使用默认值
        if ( min === Infinity || max === -Infinity ) {
            return {
                yAxisDomain: [ 0, 100 ] as [ number, number ],
                yAxisTicks: [ 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100 ],
            };
        }

        // 计算差距
        const gap = calc( "max - min | !n", { max, min } ) as number;

        // 2. 确定显示范围（domain）
        let domainMin: number;
        let domainMax: number;

        if ( gap < 50 ) {
            // 差距 < 50%：使用50%范围，居中显示
            const center = calc( "(min + max) / 2 | !n", { min, max } ) as number;
            let tempMin = calc( "center - 25 | !n", { center } ) as number;
            let tempMax = calc( "center + 25 | !n", { center } ) as number;

            // 边界检查
            if ( tempMin < 0 ) {
                tempMin = 0;
                tempMax = 50;
            } else if ( tempMax > 100 ) {
                tempMax = 100;
                tempMin = 50;
            }

            // 取整优化：调整domain边界为10的倍数
            domainMin = Math.floor( tempMin / 10 ) * 10;
            domainMax = Math.ceil( tempMax / 10 ) * 10;

            // 确保在[0, 100]范围内
            domainMin = Math.max( 0, domainMin );
            domainMax = Math.min( 100, domainMax );

            // 如果调整后范围 < 50%，则扩展为50%（保持居中）
            const adjustedRange = calc( "domainMax - domainMin | !n", { domainMax, domainMin } ) as number;
            if ( adjustedRange < 50 ) {
                const newCenter = calc( "(domainMin + domainMax) / 2 | !n", { domainMin, domainMax } ) as number;
                domainMin = Math.max( 0, calc( "newCenter - 25 | !n", { newCenter } ) as number );
                domainMax = Math.min( 100, calc( "newCenter + 25 | !n", { newCenter } ) as number );
            }
        } else {
            // 差距 >= 50%：使用实际范围
            // 取整优化：调整domain边界，使得范围能被5整除（6条tick分隔出5个区间）
            const range = calc( "max - min | !n", { max, min } ) as number;
            const adjustedRange = Math.ceil( range / 5 ) * 5; // 向上取整到5的倍数
            const center = calc( "(min + max) / 2 | !n", { min, max } ) as number;
            let tempMin = calc( "center - adjustedRange / 2 | !n", { center, adjustedRange } ) as number;
            let tempMax = calc( "center + adjustedRange / 2 | !n", { center, adjustedRange } ) as number;

            // 边界检查
            if ( tempMin < 0 ) {
                tempMin = 0;
                tempMax = adjustedRange;
            } else if ( tempMax > 100 ) {
                tempMax = 100;
                tempMin = calc( "100 - adjustedRange | !n", { adjustedRange } ) as number;
            }

            // 确保domainMin和domainMax都是整数，并且范围是5的倍数
            domainMin = Math.round( tempMin );
            domainMax = Math.round( tempMax );

            // 如果取整后范围不是5的倍数，调整domainMax使其成为5的倍数
            const roundedRange = calc( "domainMax - domainMin | !n", { domainMax, domainMin } ) as number;
            const roundedAdjustedRange = Math.ceil( roundedRange / 5 ) * 5;
            domainMax = calc( "domainMin + roundedAdjustedRange | !n", { domainMin, roundedAdjustedRange } ) as number;

            // 再次边界检查
            if ( domainMax > 100 ) {
                domainMax = 100;
                domainMin = calc( "100 - roundedAdjustedRange | !n", { roundedAdjustedRange } ) as number;
                if ( domainMin < 0 ) {
                    domainMin = 0;
                }
            }
        }

        // 确保domain在[0, 100]范围内
        domainMin = Math.max( 0, Math.min( 100, domainMin ) );
        domainMax = Math.max( 0, Math.min( 100, domainMax ) );

        const domain = [ domainMin, domainMax ] as [ number, number ];
        const domainRange = calc( "domainMax - domainMin | !n", { domainMax, domainMin } ) as number;

        // 3. 计算ticks（分隔线）
        let ticks: number[];

        if ( domainRange <= 50 ) {
            // 范围 ≤ 50%：每10%一条，最多6条
            ticks = [];
            for ( let i = domainMin; i <= domainMax; i += 10 ) {
                if ( i <= 100 ) {
                    ticks.push( i );
                }
            }
            // 确保最多6条
            if ( ticks.length > 6 ) {
                ticks = ticks.slice( 0, 6 );
            }
        } else {
            // 范围 > 50%：均分，最多6条（分隔出5个区间）
            ticks = [];
            const step = calc( "domainRange / 5 | !n", { domainRange } ) as number;
            for ( let i = 0; i <= 5; i++ ) {
                const tickValue = calc( "domainMin + step * i | !n", { domainMin, step, i } ) as number;
                // 由于domainMin和domainRange都是整数，且domainRange是5的倍数，所以step是整数，tickValue也应该是整数
                ticks.push( Math.round( tickValue ) );
            }
        }

        return { yAxisDomain: domain, yAxisTicks: ticks };
    }, [ chartData, series ] );

    // 更新第一个数据点的引用
    useEffect( () => {
        firstDataPointRef.current = chartData.length > 0 ? chartData[0] : null;
    }, [ chartData ] );

    // 获取默认数据（最新数据点）
    const defaultData = useMemo( () => {
        if ( chartData.length === 0 ) return null;
        return chartData[chartData.length - 1];
    }, [ chartData ] );

    // 初始化默认数据
    useEffect( () => {
        if ( defaultData ) {
            // 获取第一个数据点（用于涨跌幅计算）
            const dataWithFirst = { ...defaultData };
            if ( firstDataPointRef.current ) {
                dataWithFirst.firstDataPoint = firstDataPointRef.current;
            }
            onColumnSelectRef.current?.( dataWithFirst );
        }
    }, [ defaultData ] );

    // Custom Tooltip
    const CustomTooltip = ( { active, payload, label }: any ) => {
        // 使用 useEffect 延迟状态更新，避免在渲染期间调用
        useEffect( () => {
            if ( active && payload && payload.length ) {
                // 构建数据点
                const dataPoint: any = { time: label };
                payload.forEach( ( entry: any ) => {
                    dataPoint[entry.dataKey] = entry.value;
                } );

                // 获取第一个数据点（用于涨跌幅计算）
                if ( firstDataPointRef.current ) {
                    dataPoint.firstDataPoint = firstDataPointRef.current;
                }

                // 只有当 label 变化时才调用
                if ( tooltipDataRef.current?.time !== label ) {
                    tooltipDataRef.current = dataPoint;
                    onColumnSelectRef.current?.( dataPoint );
                }
            }
        }, [ active, label ] );

        if ( !active || !payload || !payload.length ) {
            return null;
        }

        const date = new Date( label * 1000 );
        const dateStr = date.toLocaleDateString( "en-US", { year: "numeric", month: "short", day: "numeric" } );
        const timeStr = date.toLocaleTimeString( "en-US", { hour: "2-digit", minute: "2-digit" } );

        const isSingleSeries = series.length === 1;

        // 如果有 sortedGuessList，按照 sortedGuessList 的顺序排序 payload
        let sortedPayload = payload;
        if ( !isSingleSeries && sortedGuessList && sortedGuessList.length > 0 ) {
            sortedPayload = [ ...payload ].sort( ( a, b ) => {
                const aIndex = sortedGuessList.findIndex( ( g ) => g.guessNo === a.dataKey );
                const bIndex = sortedGuessList.findIndex( ( g ) => g.guessNo === b.dataKey );
                // 如果找不到，放在最后
                if ( aIndex === -1 && bIndex === -1 ) return 0;
                if ( aIndex === -1 ) return 1;
                if ( bIndex === -1 ) return -1;
                return aIndex - bIndex;
            } );
        }

        return (
            <div className="bg-surface-card p-3 rounded-lg shadow-lg border border-stroke min-w-[200px] max-w-[280px]">
                {/* 时间 */}
                <p className="text-xs text-t1 mb-2 font-[500]">{dateStr} {timeStr}</p>

                {/* 分割线 */}
                <div className="h-px bg-stroke mb-2" />

                {/* 数据项 */}
                {isSingleSeries
                    ? (
                        // 单项：小圆圈 + Yes + 百分比
                        <div className="flex items-center gap-2 mt-3">
                            <div
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: payload[0].color }}
                            />
                            <span className="text-[13px] font-medium text-t1">Yes</span>
                            <span className="text-[13px] font-medium text-t1 flex-shrink-0">
                                {payload[0].value?.toFixed( 1 )}%
                            </span>
                        </div>
                    )
                    : (
                        // 多项：每个统计项显示小圆圈 + 标题 + 百分比
                        <div className="flex flex-col gap-3 mt-3">
                            {sortedPayload.map( ( entry: any, index: number ) => {
                                const seriesItem = series.find( s => s.name === entry.dataKey );
                                const title = seriesItem?.title || entry.dataKey;
                                return (
                                    <div key={index} className="flex items-center gap-2 min-w-0">
                                        <div
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: entry.color }}
                                        />
                                        <span className="text-[13px] font-medium text-t1 truncate flex-1 min-w-0">
                                            {title}
                                        </span>
                                        <span className="text-[13px] font-medium text-t1 flex-shrink-0">
                                            {entry.value?.toFixed( 1 )}%
                                        </span>
                                    </div>
                                );
                            } )}
                        </div>
                    )}
            </div>
        );
    };

    return (
        <div className="mb-3 w-full">
            {/* Chart container */}
            <div
                ref={chartContainerRef}
                className="relative overflow-hidden rounded-xl bg-surface-card px-2 pt-2 pb-1 [&_svg]:outline-none [&_svg_*]:outline-none [&_*]:focus:outline-none [-webkit-tap-highlight-color:transparent]"
                style={{ minHeight: "16px" }}
            >
                {/* Loading state */}
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-surface-card/80 z-10 rounded-xl">
                        <LoadingSpinner size="lg" />
                    </div>
                )}

                {/* 预览模式占位符 */}
                {isPreview && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-surface-subtle">
                        <svg
                            className="w-15 h-15 text-content-subtle mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                            />
                        </svg>
                        <p className="text-sm text-t3">Chart unavailable in preview mode</p>
                        <p className="text-xs text-content-subtle mt-1">
                            Real-time data will be shown after publishing
                        </p>
                    </div>
                )}

                {/* Chart */}
                <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                        data={chartData}
                        margin={{ top: 12, right: 4, left: 0, bottom: 8 }}
                        style={{ WebkitTapHighlightColor: "transparent" }}
                    >
                        <CartesianGrid
                            strokeDasharray="1 4"
                            stroke="var(--ui-stroke-muted)"
                            strokeWidth={1}
                            vertical={false}
                        />
                        <XAxis dataKey="time" hide />
                        <YAxis
                            orientation="right"
                            domain={yAxisDomain}
                            ticks={yAxisTicks}
                            tickFormatter={( value ) => `${value}%`}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: "12px", fill: "var(--ui-content-t4)", fontWeight: 500 }}
                            width={40}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {series.map( ( s ) => (
                            <Line
                                key={s.name}
                                type="monotone"
                                dataKey={s.name}
                                stroke={s.color}
                                strokeWidth={2.5}
                                dot={false}
                                activeDot={{ r: 5, stroke: "none", strokeWidth: 0 }}
                                connectNulls
                            />
                        ) )}
                    </LineChart>
                </ResponsiveContainer>

                {/* Chart bottom info — 时间范围：Figma 818-32211 胶囊选中态（卡内底栏） */}
                <div className="flex h-9 items-center justify-center border-t border-stroke px-1">
                    <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5">
                        {timeRanges.map( ( range ) => (
                            <button
                                key={range}
                                type="button"
                                onClick={() => handleTimeRangeClick( range )}
                                className={cn(
                                    "rounded-full px-2.5 py-1 text-xs font-medium outline-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-t1/20",
                                    selectedTimeRange === range ? "bg-surface-muted text-t1" : "text-t3 hover:text-t2",
                                )}
                            >
                                {range}
                            </button>
                        ) )}
                        {/* Filter icon */}
                        {onFilterClick && (
                            <button
                                type="button"
                                onClick={onFilterClick}
                                className="flex size-5 items-center justify-center transition-opacity hover:opacity-70 outline-none focus:outline-none"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11.6667 8.33333H2.5V10H11.6667V8.33333ZM11.6667 5H2.5V6.66667H11.6667V5ZM15 11.6667V8.33333H13.3333V11.6667H10V13.3333H13.3333V16.6667H15V13.3333H18.3333V11.6667H15ZM2.5 13.3333H8.33333V11.6667H2.5V13.3333Z"
                                        fill="var(--ui-content-t1)"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom divider */}
            {/* <div className="h-px bg-stroke" /> */}
        </div>
    );
}

export default memo( EventChart );
